import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Divider,
  MenuItem,
  Modal, 
  TextField, 
  Typography
} from '@mui/material';

import AmountTextField from '../../../../components/textFields/AmountTextField';
import useCurrencyService from '../../../../components/hooks/useCurrencyService';
import LoadingButton from '../../../../components/buttons/Button';

import { 
  CreatePlanAddonBIZsubs, 
  UpdatePlanAddonBIZsubs 
} from '../../../../redux/actions/business/subscriptions/planAddonsActions';
import {  
  GetSubscriptionPlansBIZsubs, 
} from '../../../../redux/actions/business/subscriptions/subscriptionPlansActions';

import global from "../../../../global.scss";

const Data = {
  AddonName: "",
  AddonDescription: "",
  Currency: "USD",
  OneTimeSetupFee: 0,
  MonthlyFee: 0,
  YearlyFee: 0,
  Plan: "",
};

const PlanAddonsModal = ({
  open, 
  setOpen, 
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.subscriptions"]);
  const { handleEditAmount, handleCalculateAmount } = useCurrencyService();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(1);

  const [subscriptionPlansList, setSubscriptionPlansList] = useState([]);

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(1);
  }

  useEffect(() => {
    try {
      dispatch(GetSubscriptionPlansBIZsubs(1, 1000, "", setLoading));
      if(open){
        if(update.defaultMode === 1){
          // mode = 1 - create plan addon
          setMode(1);
        }else if(update.defaultMode === 2){
          // mode = 2 - edit plan addon
          setMode(2);
          setFormData({
            ...formData,
            AddonName: update.addon_name || "",
            AddonDescription: update.addon_description || "",
            Currency: update.currency || "USD",
            OneTimeSetupFee: handleEditAmount(update.currency, update.one_time_setup_fee || 0),
            MonthlyFee: handleEditAmount(update.currency, update.monthly_fee || 0),
            YearlyFee: handleEditAmount(update.currency, update.yearly_fee || 0),
            Plan: update.ref_plan_id || "",
          })
        }else{
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    try {
      formatSubscriptionPlansList(state.subscriptionPlansList.records || []);
    } catch (err) {}
  }, [state]);

  const formatSubscriptionPlansList = (list) => {
    setSubscriptionPlansList(list.filter(i => i.is_active === true) || []);
  };

  // --------------- handle change ----------------
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  const handleSubmit = () => {
    if(mode === 1){
      // create plan addon
      let obj = {
        addon_name: formData.AddonName || "",
        addon_description: formData.AddonDescription || "",
        currency: formData.Currency || "",
        one_time_setup_fee: handleCalculateAmount(formData.Currency, formData.OneTimeSetupFee || 0),
        monthly_fee: handleCalculateAmount(formData.Currency, formData.MonthlyFee || 0),
        yearly_fee: handleCalculateAmount(formData.Currency, formData.YearlyFee || 0),
        ref_plan_id: formData.Plan || "",
      };
      dispatch(CreatePlanAddonBIZsubs(obj, setLoading)).then(() => {
        handleGet().then(() => {
          handleClose();
        })
      });
    }else if(mode === 2){
      // update plan addon
      let obj = {
        addon_name: formData.AddonName || "",
        addon_description: formData.AddonDescription || "",
        currency: formData.Currency || "",
        one_time_setup_fee: handleCalculateAmount(formData.Currency, formData.OneTimeSetupFee || 0),
        monthly_fee: handleCalculateAmount(formData.Currency, formData.MonthlyFee || 0),
        yearly_fee: handleCalculateAmount(formData.Currency, formData.YearlyFee || 0),
        ref_plan_id: formData.Plan || "",
      };
      dispatch(UpdatePlanAddonBIZsubs(update.id, obj, setLoading)).then(() => {
        handleGet().then(() => {
          handleClose();
        })
      });
    }else{
      toast.error("Invalid modal");
      handleClose();
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow"
        width={"100vw"} height={"100vh"}>
        <Box maxWidth={"sm"} className="modal">
          <Box width={"100%"}
            sx={{ padding: "15px"}}>
            <Typography variant='h6' sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                label="Addon name"
                fullWidth
                name="AddonName"
                value={formData.AddonName}
                onChange={handleChange}
                inputProps={{ maxLength: 255 }}
                />
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                label="Addon description"
                fullWidth
                name="AddonDescription"
                value={formData.AddonDescription}
                onChange={handleChange}
                inputProps={{ maxLength: 255 }}
                />
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <AmountTextField 
                currencyName="Currency"
                currency={formData.Currency}
                onCurrencyChange={handleChange}
                label="One time setup fee"
                fullWidth
                name="OneTimeSetupFee"
                value={formData.OneTimeSetupFee}
                onChange={handleChange}
                />
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <AmountTextField 
                currencyName="Currency"
                currency={formData.Currency}
                onCurrencyChange={handleChange}
                label="Monthly fee"
                fullWidth
                name="MonthlyFee"
                value={formData.MonthlyFee}
                onChange={handleChange}
                />
              <AmountTextField 
                currencyName="Currency"
                currency={formData.Currency}
                onCurrencyChange={handleChange}
                label="Yearly fee"
                fullWidth
                name="YearlyFee"
                value={formData.YearlyFee}
                onChange={handleChange}
                />
            </Box>
            <Box 
              className="flexCenterSBRow"
              gap={"15px"}
              mb={"15px"}>
              <TextField 
                select
                label="Subscription plan"
                fullWidth
                name="Plan"
                value={formData.Plan}
                onChange={handleChange}
                inputProps={{ maxLength: 255 }}>
                {
                  subscriptionPlansList.map((item, index) => (
                    <MenuItem
                      key={index}
                      value={item.id}>
                      {item.plan_name}
                    </MenuItem>
                  ))
                }
              </TextField>
            </Box>
          </Box>
          <Divider
           sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }}/>
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px"}}>
            <Button variant='text'
              onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton 
              variant='contained'
              loading={loading}
              disabled={!(formData.PlanName !== "" && formData.Tenant !== "")}
              onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default PlanAddonsModal;